import { t } from "i18next";
import { Check, ChevronDown, ChevronsUpDown } from "lucide-react";
import type { JSX } from "react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command/index";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface ComboboxProps {
  items: { value: string; label: string }[];
  placeholder?: string;
  onValueChange: ({ value, label }: { value: string; label: string }) => void;
  value?: string;
  disabled?: boolean;
  // Restyles the trigger and the list (e.g. the redesign v3.2 field).
  triggerClassName?: string;
  contentClassName?: string;
  // "down": the same chevron as a Select, for forms that mix the two.
  chevron?: "upDown" | "down";
}

export function Combobox({
  items,
  placeholder = t("components.combobox.commandEmpty"),
  onValueChange,
  value,
  disabled = false,
  triggerClassName,
  contentClassName,
  chevron = "upDown",
}: ComboboxProps): JSX.Element {
  const [open, setOpen] = React.useState(false);

  return (
    <div className="flex flex-col">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            aria-expanded={open}
            className={cn(
              "w-full text-base justify-between text-grey-400 bg-tinted-grey-1 h-11 border border-tinted-grey-2",
              triggerClassName,
            )}
            disabled={disabled}
            role="combobox"
            variant="outline"
          >
            {items.find((item) => item.value === value)?.label || placeholder}
            {chevron === "down" ? (
              <ChevronDown className="size-4 opacity-50" />
            ) : (
              <ChevronsUpDown className="opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className={cn("p-0", contentClassName)}>
          <Command>
            <CommandInput className="h-9" placeholder={placeholder} />
            <CommandList>
              <CommandEmpty>
                {t("components.combobox.commandEmpty")}
              </CommandEmpty>
              <CommandGroup>
                {items.map((item) => (
                  <CommandItem
                    key={item.value}
                    value={item.label}
                    onSelect={(currentValue) => {
                      const selectedItem = items.find(
                        (item) => item.label === currentValue,
                      );
                      if (selectedItem) {
                        onValueChange({
                          value: selectedItem.value,
                          label: selectedItem.label,
                        });
                      }
                      setOpen(false);
                    }}
                  >
                    {item.label}
                    <Check
                      className={cn(
                        "ml-auto",
                        value === item.value ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
